import {
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  ArenaSparChatRequest,
  ArenaSparChatResponse,
  ChatMessage,
} from '@arena/shared';
import { SceneBack } from '@/shared/components/SceneBack';
import { findCharacter, portraitFor } from '../data/characters';
// Shared stage/bubble/composer chrome with WatchDebate so the spar screen
// reads as a one-sided variant of the same arena.
import './WatchDebate.css';
import './SparChat.css';

// Same-origin by default; Vite proxies /api/* to wrangler dev. Avoids
// cross-origin permission prompts on Safari / macOS / firewalls.
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/**
 * Speech-paced reveal — matches WatchDebate. Base rate is natural
 * reading-aloud cadence; punctuation pauses sell the "speaking" feel.
 */
const REVEAL_BASE_MS = 55; // ~18 cps base
const REVEAL_PAUSE_SENTENCE_MS = 380; // after . ? !
const REVEAL_PAUSE_CLAUSE_MS = 160; // after , ; :

/**
 * Hidden user-side nudge that lets the user press SPACE to make the
 * adversary continue elaborating without typing a new question. Stored
 * in the message history (so the API sees alternating user/assistant
 * turns), filtered out of the visible transcript.
 */
const CONTINUE_NUDGE = 'Go on.';

interface LocalMessage extends ChatMessage {
  /** True for hidden continuation nudges (not shown in the transcript). */
  _continue?: boolean;
}

interface StreamingState {
  /** The reply text returned by /api/arena/spar/chat. */
  fullText: string;
  /** Chars currently visible — driven by the speech-paced typewriter. */
  reveal: number;
  /** Are we awaiting the LLM response, or now revealing it? */
  phase: 'fetching' | 'revealing';
}

/**
 * Sparring chat screen — /arena/spar/:id.
 *
 * One-sided variant of WatchDebate: a single figure portrait on the left,
 * transcript bubbles + composer in the center. The user can interrupt the
 * in-flight reply by typing a new prompt at any time — the partial is
 * committed to the transcript and the new round begins.
 *
 * After each reply, the composer blurs and the conversation waits on the
 * user — pressing SPACE sends a hidden "go on" nudge so the adversary
 * keeps elaborating; typing a new prompt aborts the wait and starts a new
 * round. Mirrors the WatchDebate UX so the two screens feel like one app.
 */
export function SparChat() {
  const { id } = useParams<{ id: string }>();
  const adversary = useMemo(() => findCharacter(id ?? null), [id]);

  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [streaming, setStreaming] = useState<StreamingState | null>(null);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [awaitingContinue, setAwaitingContinue] = useState(false);

  const streamingRef = useRef<StreamingState | null>(null);
  const messagesRef = useRef<LocalMessage[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Free-scroll the transcript: only auto-scroll on streaming updates when
  // the user is already near the bottom.
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    streamingRef.current = streaming;
  }, [streaming]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    document.title = adversary
      ? `Sparring ${adversary.name} — The Arena`
      : 'Sparring — The Arena';
  }, [adversary]);

  // Track stick-to-bottom: detach when the user scrolls up past the threshold,
  // re-attach when they scroll back into it.
  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (!el) return;
    const PIN_THRESHOLD_PX = 80;
    const update = () => {
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceFromBottom <= PIN_THRESHOLD_PX;
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    return () => el.removeEventListener('scroll', update);
  }, []);

  // Auto-scroll only when the user is following along at the bottom.
  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (!el) return;
    if (!stickToBottomRef.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages.length, streaming?.reveal, streaming?.phase]);

  // Cancel any in-flight request on unmount.
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  // Speech-paced typewriter. Same pattern as WatchDebate — irregular cadence
  // driven by setTimeout so we can pause on sentence/clause-ending punctuation.
  useEffect(() => {
    if (!streaming || streaming.phase !== 'revealing') return;
    let cancelled = false;
    let timeoutId: number | undefined;

    const step = () => {
      if (cancelled) return;
      const s = streamingRef.current;
      if (!s || s.phase !== 'revealing') return;
      if (s.reveal >= s.fullText.length) return;

      const justRevealed = s.fullText[s.reveal];
      setStreaming((cur) =>
        cur ? { ...cur, reveal: cur.reveal + 1 } : cur,
      );

      let delay = REVEAL_BASE_MS;
      if (justRevealed && /[.?!…]/.test(justRevealed)) {
        delay += REVEAL_PAUSE_SENTENCE_MS;
      } else if (justRevealed && /[,;:—]/.test(justRevealed)) {
        delay += REVEAL_PAUSE_CLAUSE_MS;
      }
      timeoutId = window.setTimeout(step, delay);
    };

    timeoutId = window.setTimeout(step, REVEAL_BASE_MS);
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [streaming?.phase]);

  // Wait for the typewriter reveal to catch up to `target` chars (or abort).
  const waitForReveal = useCallback(
    (target: number, signal: AbortSignal) =>
      new Promise<void>((resolve) => {
        const check = () => {
          if (signal.aborted) return resolve();
          const s = streamingRef.current;
          if (!s) return resolve();
          if (s.reveal >= target) return resolve();
          setTimeout(check, 60);
        };
        check();
      }),
    [],
  );

  /**
   * Tear down the in-flight reply (if any) and commit whatever the user
   * has actually seen so far as a partial message. Returns the updated
   * messages array so the caller can chain without waiting on React.
   */
  const interruptAndCommit = (): LocalMessage[] => {
    const current = streamingRef.current;
    const base = messagesRef.current;
    if (!current) return base;

    abortRef.current?.abort();
    setStreaming(null);
    setAwaitingContinue(false);

    const heard = current.fullText.slice(0, current.reveal).trim();
    if (!heard) return base;
    const partial: LocalMessage = {
      role: 'assistant',
      content: heard.replace(/[.?!,—\s]+$/u, '') + ' —',
    };
    const next = [...base, partial];
    setMessages(next);
    messagesRef.current = next;
    return next;
  };

  /**
   * Send the full conversation (including any hidden continue nudges) to
   * the API and return the assistant's reply text. The `_continue` flag is
   * stripped before sending so the server sees a clean ChatMessage[].
   */
  const fetchReply = useCallback(
    async (
      history: LocalMessage[],
      signal: AbortSignal,
    ): Promise<string | null> => {
      if (!adversary) return null;
      const body: ArenaSparChatRequest = {
        characterId: adversary.id,
        messages: history.map(({ _continue: _ignored, ...rest }) => rest),
      };
      const res = await fetch(`${API_BASE}/api/arena/spar/chat`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as {
          error?: string;
          detail?: string;
        };
        throw new Error(
          payload.detail ?? payload.error ?? `HTTP ${res.status}`,
        );
      }
      const data = (await res.json()) as ArenaSparChatResponse;
      return data.message.content;
    },
    [adversary],
  );

  const handleSubmit = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault();
      if (!adversary) return;
      const text = input.trim();
      if (!text) return;
      setError(null);

      const baseAfterInterrupt = interruptAndCommit();
      if (streamingRef.current === null && abortRef.current?.signal.aborted) {
        await new Promise((r) => setTimeout(r, 30));
      }

      let working: LocalMessage[] = [
        ...baseAfterInterrupt,
        { role: 'user', content: text },
      ];
      setMessages(working);
      messagesRef.current = working;
      setInput('');

      // Blur the composer so the user can immediately press space to
      // continue without first clicking out of the input.
      inputRef.current?.blur();

      // New round → re-pin to bottom so the user sees the new content.
      stickToBottomRef.current = true;

      const abort = new AbortController();
      abortRef.current = abort;

      // Run replies in a loop: first reply auto-plays; every subsequent
      // reply waits for the user to press SPACE. Pressing space appends
      // a hidden "Go on." nudge so the adversary keeps developing the
      // thought. Typing a new prompt + Enter aborts the wait.
      while (true) {
        setStreaming({ fullText: '', reveal: 0, phase: 'fetching' });

        let replyText: string;
        try {
          const reply = await fetchReply(working, abort.signal);
          if (abort.signal.aborted) return;
          replyText = (reply ?? '').trim();
        } catch (err) {
          if (abort.signal.aborted) return;
          setError(err instanceof Error ? err.message : 'Request failed');
          setStreaming(null);
          return;
        }

        if (!replyText) {
          setStreaming(null);
          return;
        }

        // Reveal at speech pace.
        setStreaming({ fullText: replyText, reveal: 0, phase: 'revealing' });
        await waitForReveal(replyText.length, abort.signal);
        if (abort.signal.aborted) return;

        // Commit the assistant turn.
        working = [
          ...working,
          { role: 'assistant', content: replyText },
        ];
        setMessages(working);
        messagesRef.current = working;
        setStreaming(null);

        // Wait for SPACE to continue (or for an interrupt).
        setAwaitingContinue(true);
        const aborted = await waitForSpace(abort.signal);
        setAwaitingContinue(false);
        if (aborted) return;

        // Append a hidden continuation nudge so the API sees a clean
        // user/assistant alternation and the adversary keeps elaborating.
        working = [
          ...working,
          { role: 'user', content: CONTINUE_NUDGE, _continue: true },
        ];
        setMessages(working);
        messagesRef.current = working;
      }
    },
    [adversary, input, fetchReply, waitForReveal],
  );

  const onInputKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  if (!adversary) {
    return (
      <main className="scene">
        <div
          className="scene__image scene__image--spar"
          role="img"
          aria-label="The arena"
        />
        <div className="scene__vignette" />
        <SceneBack />
        <div className="scene__content">
          <p className="scene__eyebrow">— Missing —</p>
          <h1 className="scene__title">No adversary chosen</h1>
          <p className="scene__subtitle">
            Return to the ring and choose someone to face.
          </p>
          <Link to="/arena/spar" className="scene__cta">
            Return
          </Link>
        </div>
      </main>
    );
  }

  const visibleStreamingText = streaming
    ? streaming.fullText.slice(0, streaming.reveal)
    : '';
  const isStreaming = !!streaming;
  const visibleMessages = messages.filter((m) => !m._continue);

  return (
    <main className="scene scene--debate scene--spar-chat">
      <div
        className="scene__image scene__image--spar"
        role="img"
        aria-label="The arena"
      />
      <div className="scene__vignette" />
      <SceneBack />

      <section className="stage stage--solo">
        <aside
          className={`stage__figure stage__figure--left${
            isStreaming ? ' is-speaking' : ''
          }`}
        >
          <div className="stage__portrait">
            <img
              src={portraitFor(adversary.id)}
              alt={adversary.name}
              loading="eager"
            />
          </div>
          <div className="stage__figure-name">
            <em>{adversary.name}</em>
            <span>{adversary.era}</span>
          </div>
        </aside>

        <div className="stage__center">
          <div className="stage__transcript" ref={transcriptScrollRef}>
            {visibleMessages.length === 0 && !streaming && (
              <p className="stage__empty">
                <em>{adversary.name}</em> is waiting. Open the exchange.
              </p>
            )}

            {visibleMessages.map((m, i) => (
              <Bubble
                key={i}
                side={m.role === 'user' ? 'right' : 'left'}
                name={m.role === 'user' ? 'You' : adversary.name}
                content={m.content}
              />
            ))}

            {streaming && (
              <Bubble
                side="left"
                name={adversary.name}
                content={visibleStreamingText}
                isStreaming
              />
            )}

            {awaitingContinue && (
              <p className="stage__continue-hint" role="status">
                <kbd>SPACE</kbd>
                <span>to continue</span>
              </p>
            )}

            {error && (
              <div className="stage__error" role="alert">
                {error}
              </div>
            )}
          </div>

          <form className="stage__composer" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              className="stage__input"
              placeholder={
                streaming
                  ? `Interrupt — speak again to ${adversary.name}…`
                  : `Speak to ${adversary.name}…`
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onInputKeyDown}
              rows={1}
              autoFocus
            />
            <button
              type="submit"
              className="stage__send"
              disabled={input.trim().length === 0}
              aria-label="Send"
            >
              <span aria-hidden="true">→</span>
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

/**
 * Wait for the user to press the spacebar OR for the signal to abort.
 * Ignores keypresses while an input/textarea has focus so the user can
 * still type a new prompt without it counting as "continue". Mirrors the
 * version in WatchDebate.
 */
function waitForSpace(signal?: AbortSignal): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve(true);

    const cleanup = () => {
      document.removeEventListener('keydown', onKey);
      signal?.removeEventListener('abort', onAbort);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      cleanup();
      resolve(false);
    };
    const onAbort = () => {
      cleanup();
      resolve(true);
    };
    document.addEventListener('keydown', onKey);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

interface BubbleProps {
  side: 'left' | 'right' | 'user';
  name: string;
  content: string;
  isStreaming?: boolean;
}

function Bubble({ side, name, content, isStreaming }: BubbleProps) {
  return (
    <article
      className={`bubble bubble--${side}${
        isStreaming ? ' bubble--streaming' : ''
      }`}
    >
      <header className="bubble__name">{name}</header>
      <div className="bubble__body">
        {content.length === 0 && isStreaming ? (
          <span className="bubble__dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        ) : (
          content
        )}
      </div>
    </article>
  );
}
